import { Row, Table, Tag, TColumn } from '@sketchnote/table';
import {
  insertNodes,
  PlateEditor,
  StyledElementProps,
  TElement,
  useEditorRef,
} from '@udecode/plate';
import cn from 'classnames';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import TextareaAutosize from 'react-textarea-autosize';
import { Editor, Path, Transforms } from 'slate';
import { ReactEditor, useReadOnly } from 'slate-react';
import { useSelected } from 'slate-react';

import { useSlatePluginsStore } from '../store';
import { FreezePane, FreezePaneProps } from './freeze-pane';
import { MoreOptions } from './more-option';
import { useFreezeApply } from './use-freeze-columns';

const { useMemo, useCallback, useState } = React;

export interface TablePlusElementProps extends StyledElementProps {
  element: TElement & { column: TColumn[]; row: Row[]; tagOptions: Tag[]; title: TElement };
}

export const TablePlusElement = ({
  attributes,
  children,
  element,
  nodeProps,
}: TablePlusElementProps): JSX.Element => {
  const editor = useEditorRef() as Editor & PlateEditor & ReactEditor;
  const readOnly = useReadOnly();

  // First user would choose freeze option from table more menu
  // Second enable the freeze
  // Third opens menu and let the user to choose option
  // 0 - Un Freeze, 1 - Freeze first col, 2 - Freeze second col
  const [isFreezePaneOpened, setIsFreezePaneOpened] = useState(false);
  const [freezeValue, setFreezeValue] = useState<FreezePaneProps['freezeValue']>(undefined);
  const [isFreezeEnabled, setIsFreezeEnabled] = useState(false);
  const { members } = useSlatePluginsStore();
  const selected = useSelected();

  const { row: rows = [], column: columns = [], tagOptions = [], title = initialValue } = element;

  const freezeColumns = useFreezeApply({ columns, freezeValue });

  const handleOnUpdateTable = useCallback(
    (params) => {
      const path = ReactEditor.findPath(editor, element);
      if (params.column) {
        params.column.map((obj) => {
          if (obj.frozen) {
            delete obj.frozen;
          }
        });
      }
      Transforms.setNodes(editor, { ...params } as any, { at: path });
    },
    [editor, element]
  );

  const table = useMemo(
    () => (
      <Table
        onUpdateTable={handleOnUpdateTable}
        columns={freezeColumns}
        rows={rows}
        tagOptions={tagOptions}
        members={members}
        isReadOnly={readOnly}
      />
    ),
    [element, handleOnUpdateTable, members, freezeColumns, readOnly]
  );

  const isTableSelected = useMemo(
    () => editor?.selection?.anchor.path !== editor?.selection?.focus.path && !!selected,
    [editor?.selection, selected]
  );

  const handleOnDelete = () => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at: path });
  };

  const handleOnDuplicate = () => {
    const path = ReactEditor.findPath(editor, element);
    const at = Path.next(path);
    const newElement = { ...element };
    if (newElement.id) {
      delete newElement.id;
    }
    insertNodes(editor, newElement, {
      at,
    });
  };

  const handleFreezeChange: FreezePaneProps['onChange'] = (value) => {
    if (value === 0) {
      setIsFreezePaneOpened(false);
      setIsFreezeEnabled(false);
      setFreezeValue(0);
    } else {
      setFreezeValue(value);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <p contentEditable={false} className="p-10 mb-20 mt-10">
          <span role="img" aria-label="Warning emoji">
            ⚠️
          </span>{' '}
          Something wrong with table! try remove and add new.
        </p>
      )}
    >
      <div {...attributes} {...nodeProps} contentEditable={false}>
        <div contentEditable={false} className="overflow-auto mt-10 mb-20">
          <div className="flex justify-between">
            <TextareaAutosize
              className="mb-14 mr-5 placeholder-gray-500 text-base flex-grow bg-transparent"
              placeholder="Untitled"
              disabled={readOnly}
              value={title?.[0]?.children?.[0].text}
              onChange={(e) => {
                handleOnUpdateTable({ title: [{ children: [{ text: e.target.value }] }] });
              }}
            />
            <div className="flex">
              {isFreezeEnabled && (
                <div className="mr-15 flex">
                  <FreezePane
                    onChange={handleFreezeChange}
                    isOpen={isFreezePaneOpened}
                    setIsOpen={setIsFreezePaneOpened}
                    freezeValue={freezeValue}
                  />
                </div>
              )}
              {!readOnly && (
                <MoreOptions
                  onFreeze={() => {
                    setIsFreezeEnabled(true);
                    setIsFreezePaneOpened(true);
                  }}
                  onDelete={handleOnDelete}
                  onDuplicate={handleOnDuplicate}
                />
              )}
            </div>
          </div>
          <div className={cn({ 'border-blue-500 border-2 rounded-md': isTableSelected })}>
            {table}
          </div>
        </div>
        {children}
      </div>
    </ErrorBoundary>
  );
};

export function getInitialValue(value) {
  if (typeof value === 'string') {
    return [
      {
        children: [{ text: value }],
      },
    ];
  }
  if (value?.length !== undefined) {
    return value;
  }
  return initialValue;
}

const initialValue: any[] = [
  {
    children: [{ text: '' }],
  },
];
